import { Tags } from "@/enum/tags";
import { Paginated } from "src/@types/paginated";
import { apiSlice } from "../http";

// Types
export interface ReceivedGnre {
    id: number;
    batch: string;
    created_at: string;
    generated_by: string | null;
    nff: string | null;
    generated_nff: boolean;
}

export interface Invoice {
    id: string;
    invoice_number: number;
    emission_date: string;
    cnpj: string;
    state: string;
    nfe_key: string;
    status: string;
    is_payed: boolean;
    received_gnre: ReceivedGnre[];
    created_at: string;
    updated_at: string;
}

export interface InvoiceItem {
    id: string;
    item_sequence: number;
    ncm_code: string;
    accounting_value: number | string;
    total_tax_value: number | string;
    tax_rate: number | string;
    created_at: string;
    updated_at: string;
    ncmSt?: {
        ncm: string;
        is_active: boolean;
        state?: string;
        iva?: string;
    };
}

export interface InvoiceFilters {
    search?: string;
    orderBy?: string;
    page?: number;
    perPage?: number;
    state?: string;
    from_date?: string;
    to_date?: string;
    is_payed?: string;
    generated_gnre?: string;
    has_ncm_assigned?: string;
}

export interface InvoicePaymentBatchStatusUpdate {
    id: string | number;
    is_payed: boolean;
}

export interface PaymentStatusUpdateResponse {
    success: boolean;
    data: Invoice;
}

export interface BatchPaymentStatusUpdateResponse {
    success: boolean;
    data: Invoice[];
}

export const taxWorkflowInformation = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        getInvoices: builder.query<Paginated<Invoice>, InvoiceFilters>({
            query: (filters) => {
                const params = new URLSearchParams();

                if (filters.search) params.append('search', filters.search);
                if (filters.orderBy) params.append('orderBy', filters.orderBy);
                if (filters.page) params.append('page', filters.page.toString());
                if (filters.perPage) params.append('perPage', filters.perPage.toString());
                if (filters.state) params.append('state', filters.state);
                if (filters.from_date) params.append('from_date', filters.from_date);
                if (filters.to_date) params.append('to_date', filters.to_date);
                if (filters.is_payed) params.append('is_payed', filters.is_payed);
                if (filters.generated_gnre) params.append('generated_gnre', filters.generated_gnre);
                if (filters.has_ncm_assigned) params.append('has_ncm_assigned', filters.has_ncm_assigned);

                return {
                    url: `tax-workflow/invoices?${params.toString()}`,
                    method: "GET",
                };
            },
            providesTags: [Tags.TAX_WORKFLOW],
        }),

        getInvoiceItemsForInvoice: builder.query<InvoiceItem[], { invoiceId: string }>({
            query: ({ invoiceId }) => ({
                url: `tax-workflow/invoices-item/${invoiceId}`,
                method: "GET",
            }),
            providesTags: [Tags.TAX_WORKFLOW],
        }),

        updateInvoicePaymentStatus: builder.mutation<
            PaymentStatusUpdateResponse,
            { id: string; is_payed: boolean }
        >({
            query: ({ id, is_payed }) => ({
                url: `tax-workflow/invoices/payment-status/${id}`,
                method: "PATCH",
                body: { is_payed },
            }),
            invalidatesTags: [Tags.TAX_WORKFLOW],
        }),

        updateInvoicePaymentStatusInBatch: builder.mutation<
            BatchPaymentStatusUpdateResponse,
            InvoicePaymentBatchStatusUpdate[]
        >({
            query: (items) => ({
                url: `tax-workflow/invoices/payment-status/batch`,
                method: "PUT",
                body: items,
            }),
            invalidatesTags: [Tags.TAX_WORKFLOW],
        }),
    }),
    overrideExisting: true,
});

export const {
    useGetInvoicesQuery,
    useLazyGetInvoiceItemsForInvoiceQuery,
    useUpdateInvoicePaymentStatusMutation,
    useUpdateInvoicePaymentStatusInBatchMutation,
} = taxWorkflowInformation;
